import React, { useEffect, useState, useRef } from 'react';
import { Box, Card, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress, TextField, MenuItem, Grid, Tabs, Tab, Collapse, IconButton, Button, Paper, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Tooltip, Badge, LinearProgress } from '@mui/material';
import { ExpandMore, ExpandLess, PictureAsPdf, Download, Print, Security, LocationOn, Devices, Warning, TrendingUp, FilterList, Refresh } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../utils/api';
import StatusChip from '../components/common/StatusChip';
import { useSocket } from '../contexts/SocketContext';

const ACTION_COLORS = {
  CREATE: 'primary', EDIT: 'warning', APPROVE: 'success', REJECT: 'error',
  SUBMIT: 'info', INITIATE: 'info', KYC_UPLOAD: 'info', DELETE: 'error',
  EDIT_REQUEST: 'info', EDIT_APPROVE: 'success', EDIT_REJECT: 'error'
};

const RISK_COLORS = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'error',
  CRITICAL: 'error'
};

const RISK_ICONS = {
  LOW: null,
  MEDIUM: <Warning fontSize="small" />,
  HIGH: <Warning fontSize="small" />,
  CRITICAL: <Security fontSize="small" />
};

function AuditRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow hover sx={log.riskLevel === 'HIGH' || log.riskLevel === 'CRITICAL' ? { bgcolor: '#fff3e0' } : {}}>
        <TableCell sx={{ fontSize:12, fontFamily:'monospace', color:'text.secondary' }}>{new Date(log.createdAt).toLocaleString()}</TableCell>
        <TableCell><Chip label={log.entityType} size="small" variant="outlined" /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={log.action} size="small" color={ACTION_COLORS[log.action] || 'default'} />
            {log.riskLevel && log.riskLevel !== 'LOW' && (
              <Tooltip title={`Risk: ${log.riskLevel}${log.anomalyDetected ? ' (Anomaly)' : ''}`}>
                <Chip 
                  label={log.riskLevel} 
                  size="small" 
                  color={RISK_COLORS[log.riskLevel]}
                  icon={RISK_ICONS[log.riskLevel]}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell><Typography fontSize={13} fontWeight={600}>{log.userId?.name || log.userName || '—'}</Typography></TableCell>
        <TableCell><Chip label={log.role || '—'} size="small" /></TableCell>
        <TableCell>
          <Tooltip title={log.ipAddress}>
            <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: log.ipAddress ? 'inherit' : 'text.secondary', fontStyle: log.ipAddress ? 'inherit' : 'italic' }}>
              {log.ipAddress || '—'}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {log.location?.country && (
            <Tooltip title={`${log.location.city}, ${log.location.country}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn fontSize="small" sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11 }}>{log.location.countryCode}</Typography>
              </Box>
            </Tooltip>
          )}
          {!log.location?.country && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>—</Typography>}
        </TableCell>
        <TableCell>
          {log.device?.browser && (
            <Tooltip title={`${log.device.browser} ${log.device.browserVersion || ''} on ${log.device.os}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Devices fontSize="small" sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11 }}>{log.device.browser}</Typography>
              </Box>
            </Tooltip>
          )}
          {!log.device?.browser && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>—</Typography>}
        </TableCell>
        <TableCell>
          {(log.oldData || log.newData || log.changedFields || log.location || log.device) ? (
            <IconButton size="small" onClick={() => setExpanded(e => !e)} color="primary">
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <Typography fontSize={11} color="text.secondary" fontStyle="italic">—</Typography>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py:0 }}>
            <Collapse in={expanded}>
              <Box sx={{ p:2, bgcolor:'#f8f9fa', borderRadius:1, my:1 }}>
                <Grid container spacing={2}>
                  {/* Geo-location & Device Info */}
                  {(log.location || log.device) && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2, borderColor: '#2196f3' }}>
                        <Box sx={{ bgcolor: '#e3f2fd', px: 1.5, py: 0.75, borderBottom: '1px solid #bbdefb' }}>
                          <Typography fontSize={12} fontWeight={700} color="#1565c0" display="flex" alignItems="center" gap={0.5}>
                            <LocationOn fontSize="small" /> LOCATION & DEVICE
                          </Typography>
                        </Box>
                        <Grid container sx={{ p: 2 }} spacing={2}>
                          {log.location && (
                            <Grid item xs={12} sm={6}>
                              <Typography fontSize={11} fontWeight={600} color="#666">Location</Typography>
                              <Typography fontSize={11}>
                                {log.location.city && `${log.location.city}, `}{log.location.region && `${log.location.region}, `}{log.location.country}
                              </Typography>
                              <Typography fontSize={10} color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {log.location.latitude && `${log.location.latitude}, ${log.location.longitude}`} {log.location.timezone && `(${log.location.timezone})`}
                              </Typography>
                            </Grid>
                          )}
                          {log.device && (
                            <Grid item xs={12} sm={6}>
                              <Typography fontSize={11} fontWeight={600} color="#666">Device</Typography>
                              <Typography fontSize={11}>{log.device.browser} {log.device.browserVersion}</Typography>
                              <Typography fontSize={11}>{log.device.os} {log.device.osVersion}</Typography>
                              <Typography fontSize={10} color="text.secondary">Type: {log.device.deviceType} | {log.device.language}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}
                  
                  {/* Risk Assessment */}
                  {log.riskLevel && log.riskLevel !== 'LOW' && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2, borderColor: log.riskLevel === 'CRITICAL' ? '#f44336' : '#ff9800' }}>
                        <Box sx={{ bgcolor: log.riskLevel === 'CRITICAL' ? '#ffebee' : '#fff3e0', px: 1.5, py: 0.75, borderBottom: '1px solid ' + (log.riskLevel === 'CRITICAL' ? '#ffcdd2' : '#ffe0b2') }}>
                          <Typography fontSize={12} fontWeight={700} color={log.riskLevel === 'CRITICAL' ? '#c62828' : '#ef6c00'} display="flex" alignItems="center" gap={0.5}>
                            <Security fontSize="small" /> RISK ASSESSMENT
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <Typography fontSize={11} fontWeight={600}>Risk Level: {log.riskLevel}</Typography>
                          {log.riskFactors && log.riskFactors.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography fontSize={10} color="text.secondary">Factors:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {log.riskFactors.map(factor => (
                                  <Chip key={factor} label={factor} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {log.anomalyDetected && log.anomalyDescription && (
                            <Typography fontSize={10} color="error" sx={{ mt: 1 }}>
                              ⚠️ {log.anomalyDescription}
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  )}

                  {/* Changed Fields */}
                  {log.changedFields && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2, borderColor: '#9c27b0' }}>
                        <Box sx={{ bgcolor: '#f3e5f5', px: 1.5, py: 0.75, borderBottom: '1px solid #e1bee7' }}>
                          <Typography fontSize={12} fontWeight={700} color="#7b1fa2" display="flex" alignItems="center" gap={0.5}>
                            <TrendingUp fontSize="small" /> CHANGED FIELDS
                          </Typography>
                        </Box>
                        <Table size="small" sx={{ '& td': { fontSize: 11, py: 0.5, borderColor: '#f5f5f5' } }}>
                          <TableBody>
                            {Object.entries(log.changedFields).map(([key, val]) => (
                              <TableRow key={key} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                <TableCell sx={{ fontWeight: 600, color: '#666', width: '30%' }}>{key}</TableCell>
                                <TableCell sx={{ color: '#f44336', fontFamily: 'monospace', fontSize: 10 }}>
                                  {typeof val.old === 'object' ? JSON.stringify(val.old) : String(val.old)}
                                </TableCell>
                                <TableCell sx={{ color: '#4caf50', fontFamily: 'monospace', fontSize: 10 }}>
                                  → {typeof val.new === 'object' ? JSON.stringify(val.new) : String(val.new)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    </Grid>
                  )}

                  {/* Old/New Data */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {log.oldData && <Grid item xs={6}>
                        <Card variant="outlined" sx={{ borderColor: '#ff9800' }}>
                          <Box sx={{ bgcolor: '#fff3e0', px: 1.5, py: 0.75, borderBottom: '1px solid #ffe0b2' }}>
                            <Typography fontSize={12} fontWeight={700} color="#e65100" display="flex" alignItems="center" gap={0.5}>
                              <span>←</span> OLD DATA
                            </Typography>
                          </Box>
                          <Table size="small" sx={{ '& td': { fontSize: 11, py: 0.5, borderColor: '#f5f5f5' } }}>
                            <TableBody>
                              {Object.entries(log.oldData).map(([key, val]) => (
                                <TableRow key={key} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                  <TableCell sx={{ fontWeight: 600, color: '#666', width: '40%' }}>{key}</TableCell>
                                  <TableCell sx={{ color: '#333', fontFamily: 'monospace' }}>
                                    {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                      </Grid>}
                      {log.newData && <Grid item xs={6}>
                        <Card variant="outlined" sx={{ borderColor: '#4caf50' }}>
                          <Box sx={{ bgcolor: '#e8f5e9', px: 1.5, py: 0.75, borderBottom: '1px solid #c8e6c9' }}>
                            <Typography fontSize={12} fontWeight={700} color="#2e7d32" display="flex" alignItems="center" gap={0.5}>
                              <span>→</span> NEW DATA
                            </Typography>
                          </Box>
                          <Table size="small" sx={{ '& td': { fontSize: 11, py: 0.5, borderColor: '#f5f5f5' } }}>
                            <TableBody>
                              {Object.entries(log.newData).map(([key, val]) => (
                                <TableRow key={key} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                  <TableCell sx={{ fontWeight: 600, color: '#666', width: '40%' }}>{key}</TableCell>
                                  <TableCell sx={{ color: '#333', fontFamily: 'monospace' }}>
                                    {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                      </Grid>}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AuditPage() {
  const [tab, setTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [printOpen, setPrintOpen] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [autoFix, setAutoFix] = useState(false);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const printRef = useRef(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const { socket } = useSocket();

  // Fetch logs with filters
  const fetchLogs = async () => {
    setLoading(true);
    const params = {};
    if (entityType) params.entityType = entityType;
    if (actionFilter) params.action = actionFilter;
    if (riskLevelFilter) params.riskLevel = riskLevelFilter;
    if (ipFilter) params.ipAddress = ipFilter;
    if (countryFilter) params.countryCode = countryFilter;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (searchQuery) params.search = searchQuery;
    
    try {
      const [lr, hr, an] = await Promise.all([
        api.get('/audit/logs', { params }),
        api.get('/audit/history'),
        api.get('/audit/analytics', { params: { startDate, endDate, entityType } })
      ]);
      setLogs(lr.data.logs);
      setHistory(hr.data.history);
      setAnalytics(an.data);
    } catch (error) {
      console.error('Error fetching audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [entityType, actionFilter, riskLevelFilter, ipFilter, countryFilter, startDate, endDate, searchQuery]);

  // Socket real-time updates
  useEffect(() => {
    if (!socket || !realTimeEnabled) return;

    // Check if socket has the on method (socket.io-client)
    if (typeof socket.on !== 'function') {
      console.warn('Socket does not have on method:', socket);
      return;
    }

    const handleAuditLogCreated = (data) => {
      setLogs(prev => [data.log, ...prev].slice(0, 100)); // Keep last 100
      if (tab === 2) {
        fetchAnalytics();
      }
    };

    socket.on('audit_log_created', handleAuditLogCreated);

    return () => {
      if (typeof socket.off === 'function') {
        socket.off('audit_log_created', handleAuditLogCreated);
      }
    };
  }, [socket, realTimeEnabled, tab]);

  const fetchAnalytics = async () => {
    try {
      const an = await api.get('/audit/analytics', { params: { startDate, endDate, entityType } });
      setAnalytics(an.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  useEffect(() => {
    if (tab === 2) {
      fetchAnalytics();
    }
  }, [tab, startDate, endDate, entityType]);

  // Export to Excel (CSV) - using backend API
  const exportToExcel = async () => {
    try {
      const params = {
        entityType,
        action: actionFilter,
        riskLevel: riskLevelFilter,
        startDate,
        endDate,
        format: 'csv'
      };
      
      const response = await api.get('/audit/export', { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const clearFilters = () => {
    setEntityType('');
    setActionFilter('');
    setRiskLevelFilter('');
    setUserFilter('');
    setIpFilter('');
    setCountryFilter('');
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
  };

  // Generate PDF (multi-page)
  const generatePDF = async () => {
    if (!printRef.current) return;

    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 190;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      let pageCount = 1;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        pageCount++;
      }

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text(`Page ${i} of ${totalPages}`, 10, 290);
        pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 140, 290);
      }

      pdf.save(`audit_report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    }
  };

  // Print
  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    if (!printContent) return;

    // Create a new window for printing
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #1a3c6e; color: white; }
          .header { border-bottom: 2px solid #1a3c6e; margin-bottom: 20px; padding-bottom: 10px; }
          .summary { background-color: #f5f5f5; padding: 15px; margin-bottom: 20px; border-radius: 5px; }
          .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 11px; color: #666; }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
      printWindow.close();
    };
  };

  // Run validation
  const runValidation = async () => {
    setValidationLoading(true);
    try {
      const response = await api.get('/audit/validate', { params: { autoFix } });
      setValidationResults(response.data);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setValidationLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Typography variant="h5" fontWeight={700} color="#1a3c6e">Audit & History</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportToExcel}
            disabled={loading || (tab === 0 ? logs.length === 0 : history.length === 0)}
            sx={{ fontSize: { xs: 11, sm: 12 } }}
          >
            Export to Excel
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdf />}
            onClick={() => setPrintOpen(true)}
            disabled={loading || (tab === 0 ? logs.length === 0 : history.length === 0)}
            sx={{ fontSize: { xs: 11, sm: 12 } }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            disabled={loading || (tab === 0 ? logs.length === 0 : history.length === 0)}
            sx={{ bgcolor: '#1a3c6e' }}
          >
            Print
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: { xs: 1, sm: 2 } }}>
        <Tab label="Audit Logs" />
        <Tab label="Status History" />
        <Tab label="Analytics Dashboard" />
        <Tab label="Validation Dashboard" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {/* Advanced Filters */}
          <Paper sx={{ mb: 2, p: { xs: 1, sm: 2 }, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label="Entity Type" value={entityType} onChange={e => setEntityType(e.target.value)} sx={{ minWidth: { xs: 100, sm: 140 } }}>
              {['','Investor','Security','Allocation','Transfer','Holding','Dividend','User'].map(e => <MenuItem key={e} value={e}>{e || 'All'}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Action" value={actionFilter} onChange={e => setActionFilter(e.target.value)} sx={{ minWidth: { xs: 100, sm: 120 } }}>
              {['','CREATE','UPDATE','DELETE','APPROVE','REJECT','SUBMIT','EDIT_REQUEST','EDIT_APPROVE','EDIT_REJECT'].map(e => <MenuItem key={e} value={e}>{e || 'All'}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Risk Level" value={riskLevelFilter} onChange={e => setRiskLevelFilter(e.target.value)} sx={{ minWidth: { xs: 100, sm: 120 } }}>
              {['','LOW','MEDIUM','HIGH','CRITICAL'].map(e => <MenuItem key={e} value={e}>{e || 'All'}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Search" placeholder="User, action, reason..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ minWidth: { xs: 120, sm: 180 } }} />
            <TextField size="small" label="IP Address" placeholder="IP..." value={ipFilter} onChange={e => setIpFilter(e.target.value)} sx={{ minWidth: { xs: 100, sm: 140 } }} />
            <TextField size="small" label="Country" placeholder="Code..." value={countryFilter} onChange={e => setCountryFilter(e.target.value)} sx={{ minWidth: { xs: 80, sm: 100 } }} />
            <TextField size="small" type="date" label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { xs: 110, sm: 150 } }} />
            <TextField size="small" type="date" label="End Date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { xs: 110, sm: 150 } }} />
            <Button variant="outlined" size="small" onClick={clearFilters} startIcon={<FilterList />} sx={{ fontSize: { xs: 11, sm: 12 } }}>Clear</Button>
            <Button variant="outlined" size="small" onClick={fetchLogs} startIcon={<Refresh />} sx={{ fontSize: { xs: 11, sm: 12 } }}>Refresh</Button>
            <Button 
              variant={realTimeEnabled ? 'contained' : 'outlined'} 
              size="small" 
              onClick={() => setRealTimeEnabled(!realTimeEnabled)}
              sx={{ fontSize: { xs: 11, sm: 12 } }}
            >
              Real-time: {realTimeEnabled ? 'ON' : 'OFF'}
            </Button>
          </Paper>
          <Card>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: { xs: 900, md: 1100 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Timestamp</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Entity</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Action</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>User</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Role</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>IP Address</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Location</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Device</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Details</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell></TableRow>}
                {!loading && logs.map(log => <AuditRow key={log._id} log={log} />)}
                {!loading && logs.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ color:'text.secondary', py:3 }}>No logs found</TableCell></TableRow>}
              </TableBody>
            </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Paper sx={{ mb: 2, p: { xs: 1, sm: 2 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" label="Filter by Changed By" placeholder="User name..." value={userFilter} onChange={e => setUserFilter(e.target.value)} sx={{ minWidth: { xs: 150, sm: 200 }, flex: 1 }} />
            <Button variant="outlined" size="small" onClick={() => setUserFilter('')} sx={{ fontSize: { xs: 11, sm: 12 } }}>Clear Filter</Button>
          </Paper>
          <Card>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: { xs: 700, md: 900 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Timestamp</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Entity</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Old Status</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>New Status</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Changed By</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Role</TableCell>
                    <TableCell sx={{ fontSize: { xs: 11, sm: 12 } }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>}
                {!loading && history.filter(h => !userFilter || h.changedByName.toLowerCase().includes(userFilter.toLowerCase())).map(h => (
                  <TableRow key={h._id} hover>
                    <TableCell sx={{ fontSize:12, fontFamily:'monospace', color:'text.secondary' }}>{new Date(h.createdAt).toLocaleString()}</TableCell>
                    <TableCell><Chip label={h.entityType} size="small" variant="outlined" /></TableCell>
                    <TableCell>{h.oldStatus ? <StatusChip status={h.oldStatus} /> : '—'}</TableCell>
                    <TableCell><StatusChip status={h.newStatus} /></TableCell>
                    <TableCell><Typography fontSize={13} fontWeight={600}>{h.changedByName}</Typography></TableCell>
                    <TableCell><Chip label={h.changedByRole} size="small" /></TableCell>
                    <TableCell sx={{ fontSize:12, color:'text.secondary' }}>{h.reason || '—'}</TableCell>
                  </TableRow>
                ))}
                {!loading && history.filter(h => !userFilter || h.changedByName.toLowerCase().includes(userFilter.toLowerCase())).length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ color:'text.secondary', py:3 }}>No history</TableCell></TableRow>}
              </TableBody>
            </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {tab === 2 && analytics && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}>
                <Typography variant="body2" color="#1565c0" fontSize={{ xs: 11, sm: 12 }}>Total Logs</Typography>
                <Typography variant="h4" fontWeight={700} color="#0d47a1" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{analytics.summary.totalLogs}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                <Typography variant="body2" color="#2e7d32" fontSize={{ xs: 11, sm: 12 }}>Unique Users</Typography>
                <Typography variant="h4" fontWeight={700} color="#1b5e20" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{analytics.summary.uniqueUsers}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                <Typography variant="body2" color="#ef6c00" fontSize={{ xs: 11, sm: 12 }}>Unique IPs</Typography>
                <Typography variant="h4" fontWeight={700} color="#e65100" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{analytics.summary.uniqueIps}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                <Typography variant="body2" color="#c62828" fontSize={{ xs: 11, sm: 12 }}>High Risk</Typography>
                <Typography variant="h4" fontWeight={700} color="#b71c1c" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{analytics.summary.highRiskCount}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fce4ec', border: '1px solid #e91e63' }}>
                <Typography variant="body2" color="#c2185b" fontSize={{ xs: 11, sm: 12 }}>Anomalies</Typography>
                <Typography variant="h4" fontWeight={700} color="#880e4f" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{analytics.summary.anomalyCount}</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Grid */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            {/* By Action */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={700} mb={2} fontSize={{ xs: 14, sm: 16 }}>Actions</Typography>
                {analytics.byAction.map(item => (
                  <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 100 }}>{item._id}</Typography>
                    <LinearProgress variant="determinate" value={(item.count / analytics.summary.totalLogs) * 100} sx={{ flex: 1, mx: 1 }} />
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 40 }}>{item.count}</Typography>
                  </Box>
                ))}
              </Card>
            </Grid>

            {/* By Role */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={700} mb={2} fontSize={{ xs: 14, sm: 16 }}>By Role</Typography>
                {analytics.byRole.map(item => (
                  <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 100 }}>{item._id}</Typography>
                    <LinearProgress variant="determinate" value={(item.count / analytics.summary.totalLogs) * 100} sx={{ flex: 1, mx: 1 }} />
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 40 }}>{item.count}</Typography>
                  </Box>
                ))}
              </Card>
            </Grid>

            {/* By Risk Level */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={700} mb={2} fontSize={{ xs: 14, sm: 16 }}>Risk Level</Typography>
                {analytics.byRiskLevel.map(item => (
                  <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip label={item._id || 'Unknown'} size="small" color={RISK_COLORS[item._id] || 'default'} sx={{ width: 80 }} />
                    <LinearProgress variant="determinate" value={(item.count / analytics.summary.totalLogs) * 100} sx={{ flex: 1, mx: 1 }} />
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 40 }}>{item.count}</Typography>
                  </Box>
                ))}
              </Card>
            </Grid>

            {/* By Country */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={700} mb={2} fontSize={{ xs: 14, sm: 16 }}>Top Countries</Typography>
                {analytics.byCountry.slice(0, 10).map(item => (
                  <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 50 }}>{item._id}</Typography>
                    <LinearProgress variant="determinate" value={(item.count / analytics.summary.uniqueIps) * 100} sx={{ flex: 1, mx: 1 }} />
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, width: 40 }}>{item.count}</Typography>
                  </Box>
                ))}
              </Card>
            </Grid>

            {/* High Risk Logs */}
            <Grid item xs={12} sm={6} md={8}>
              <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={700} mb={2} fontSize={{ xs: 14, sm: 16 }}>High Risk Activity</Typography>
                {analytics.highRiskLogs.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 11 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>Action</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>User</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>Risk</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>IP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.highRiskLogs.map(log => (
                          <TableRow key={log._id} hover>
                            <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>{new Date(log.createdAt).toLocaleString()}</TableCell>
                            <TableCell><Chip label={log.action} size="small" /></TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{log.userName}</TableCell>
                            <TableCell><Chip label={log.riskLevel} size="small" color={RISK_COLORS[log.riskLevel]} /></TableCell>
                            <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>{log.ipAddress}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ color: 'text.secondary', py: 2 }}>No high-risk activity</Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {tab === 2 && !analytics && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {tab === 3 && (
        <Box>
          {/* Controls */}
          <Paper sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1, sm: 2 }, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
            <Button
              variant="contained"
              onClick={runValidation}
              disabled={validationLoading}
              sx={{ bgcolor: '#1a3c6e', fontSize: { xs: 11, sm: 12 }, flex: { xs: 1, sm: 'auto' } }}
            >
              {validationLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Run Audit
            </Button>
            <Button
              variant={autoFix ? 'contained' : 'outlined'}
              onClick={() => setAutoFix(!autoFix)}
              color={autoFix ? 'warning' : 'primary'}
              sx={{ fontSize: { xs: 11, sm: 12 }, flex: { xs: 1, sm: 'auto' } }}
            >
              Auto-Fix: {autoFix ? 'ON' : 'OFF'}
            </Button>
            <Button variant="outlined" onClick={() => { setValidationResults(null); }} sx={{ fontSize: { xs: 11, sm: 12 }, flex: { xs: 1, sm: 'auto' } }}>
              Refresh
            </Button>
          </Paper>

          {/* Summary Cards */}
          {validationResults && (
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}>
                  <Typography variant="body2" color="#1565c0" fontSize={{ xs: 11, sm: 12 }}>Total Checked</Typography>
                  <Typography variant="h4" fontWeight={700} color="#0d47a1" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{validationResults.summary.totalChecked}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                  <Typography variant="body2" color="#c62828" fontSize={{ xs: 11, sm: 12 }}>Errors Found</Typography>
                  <Typography variant="h4" fontWeight={700} color="#b71c1c" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{validationResults.summary.errorsFound}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                  <Typography variant="body2" color="#2e7d32" fontSize={{ xs: 11, sm: 12 }}>Auto Fixed</Typography>
                  <Typography variant="h4" fontWeight={700} color="#1b5e20" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{validationResults.summary.autoFixed}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                  <Typography variant="body2" color="#ef6c00" fontSize={{ xs: 11, sm: 12 }}>High Severity</Typography>
                  <Typography variant="h4" fontWeight={700} color="#e65100" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{validationResults.summary.highErrors}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fce4ec', border: '1px solid #e91e63' }}>
                  <Typography variant="body2" color="#c2185b" fontSize={{ xs: 11, sm: 12 }}>Critical Errors</Typography>
                  <Typography variant="h4" fontWeight={700} color="#880e4f" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>{validationResults.summary.criticalErrors}</Typography>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Issues Table */}
          {validationResults && validationResults.issues.length > 0 && (
            <Card>
              <Typography variant="h6" fontWeight={700} mb={{ xs: 1, sm: 2 }}>Validation Issues</Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 800, md: 1000 } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1a3c6e' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Type</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Severity</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Investor</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>ISIN</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Expected</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Actual</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Status</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResults.issues.map(issue => (
                      <TableRow key={issue.id} hover>
                        <TableCell sx={{ fontSize: { xs: 10, sm: 11 } }}>{issue.type}</TableCell>
                        <TableCell>
                          <Chip 
                            label={issue.severity} 
                            size="small"
                            color={issue.severity === 'CRITICAL' ? 'error' : issue.severity === 'HIGH' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: 10, sm: 11 } }}>{issue.investor}</TableCell>
                        <TableCell sx={{ fontSize: { xs: 10, sm: 11 } }}>{issue.isin}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: { xs: 10, sm: 11 } }}>{issue.expected}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: { xs: 10, sm: 11 } }}>{issue.actual}</TableCell>
                        <TableCell>
                          <Chip 
                            label={issue.fixed ? 'Fixed' : 'Pending'} 
                            size="small"
                            color={issue.fixed ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: 10, sm: 11 } }}>{issue.fixAction}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {!validationResults && (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body1" color="text.secondary">
                Click "Run Audit" to perform a full system validation check
              </Typography>
            </Card>
          )}
        </Box>
      )}

      {/* Print/Export Dialog */}
      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="lg" fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle fontWeight={700}>
          {tab === 0 ? 'Audit Logs Report' : 'Status History Report'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box ref={printRef} id="print-content" sx={{ bgcolor: '#fff', p: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #1a3c6e' }}>
              <Typography variant="h5" fontWeight={800} color="#1a3c6e">
                {tab === 0 ? 'Audit Logs Report' : 'Status History Report'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" mt={1}>
                Investor Management System - Audit Trail
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Generated on: {new Date().toLocaleDateString('en-IN')} at {new Date().toLocaleTimeString('en-IN')}
              </Typography>
              {entityType && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="primary">
                    Filter: Entity Type = {entityType}
                  </Typography>
                </Box>
              )}
              {userFilter && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="primary">
                    Filter: User = {userFilter}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">Total Records: <strong>{tab === 0 ? (userFilter ? logs.filter(log => (log.userId?.name || log.userName || '').toLowerCase().includes(userFilter.toLowerCase())).length : logs.length) : (userFilter ? history.filter(h => h.changedByName.toLowerCase().includes(userFilter.toLowerCase())).length : history.length)}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Report Type: <strong>{tab === 0 ? 'Audit Logs' : 'Status History'}</strong></Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1a3c6e' }}>
                    {tab === 0 ? (
                      <>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Timestamp</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Entity Type</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Action</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Performed By</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>IP Address</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Reason</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Timestamp</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Entity Type</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Old Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>New Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Changed By</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Reason</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tab === 0 && logs.filter(log => !userFilter || (log.userId?.name || log.userName || '').toLowerCase().includes(userFilter.toLowerCase())).map(log => (
                    <TableRow key={log._id}>
                      <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{log.entityType}</TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.userId?.name || log.userName || '—'}</TableCell>
                      <TableCell>{log.role || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>{log.ipAddress || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 10 }}>{log.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {tab === 1 && history.filter(h => !userFilter || h.changedByName.toLowerCase().includes(userFilter.toLowerCase())).map(h => (
                    <TableRow key={h._id}>
                      <TableCell sx={{ fontSize: 10, fontFamily: 'monospace' }}>
                        {new Date(h.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{h.entityType}</TableCell>
                      <TableCell>{h.oldStatus || '—'}</TableCell>
                      <TableCell>{h.newStatus}</TableCell>
                      <TableCell>{h.changedByName}</TableCell>
                      <TableCell>{h.changedByRole}</TableCell>
                      <TableCell sx={{ fontSize: 10 }}>{h.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd', fontSize: 11, color: '#666' }}>
              <Typography>
                This is an auto-generated report from the Investor Management System Audit Trail.
                For any discrepancies, please contact the system administrator.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPrintOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            onClick={generatePDF}
            sx={{ bgcolor: '#c62828' }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
